import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Download,
  FileText,
  Ship,
  DollarSign,
  Filter,
  Package,
  X,
  Check
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as XLSX from 'xlsx';

// Multi-select dropdown component
const MultiSelectDropdown = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const displayText = selected.length === 0
    ? `All ${label}`
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white hover:bg-slate-50 min-w-[140px]"
      >
        <span className={selected.length > 0 ? 'text-slate-800' : 'text-slate-500'}>{displayText}</span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">No options</div>
            ) : (
              options.map(option => (
                <button
                  key={option}
                  onClick={() => toggleOption(option)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50"
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected.includes(option) ? 'bg-blue-500 border-blue-500' : 'border-slate-300'}`}>
                    {selected.includes(option) && <Check size={12} className="text-white" />}
                  </div>
                  <span className="truncate">{option}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default function MissingBrokerData({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState([]);
  // Multi-select filters
  const [filterMonths, setFilterMonths] = useState([]);
  const [filterDivisions, setFilterDivisions] = useState([]);
  const [filterWarehouses, setFilterWarehouses] = useState([]);
  const [filterTypes, setFilterTypes] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getMissingBrokerData = httpsCallable(functions, 'getMissingBrokerData', { timeout: 150000 });
      const result = await getMissingBrokerData({ month: null });

      if (result.data.success) {
        setData(result.data.data || []);
        setSummary(result.data.summary || []);
      } else {
        setError('Failed to load data');
      }
    } catch (err) {
      console.error('Error loading missing broker data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const months = [...new Set(data.map(d => d.receipt_date?.substring(0, 7)).filter(Boolean))].sort().reverse();
    const divisions = [...new Set(data.map(d => d.division).filter(Boolean))].sort();
    const warehouses = [...new Set(data.map(d => d.warehouse).filter(Boolean))].sort();
    const types = [...new Set(data.map(d => d.product_type).filter(Boolean))].sort();
    return { months, divisions, warehouses, types };
  }, [data]);

  // Apply filters
  const filteredData = useMemo(() => {
    return data.filter(row => {
      if (filterMonths.length > 0) {
        const rowMonth = row.receipt_date?.substring(0, 7);
        if (!filterMonths.includes(rowMonth)) return false;
      }
      if (filterDivisions.length > 0 && !filterDivisions.includes(row.division)) return false;
      if (filterWarehouses.length > 0 && !filterWarehouses.includes(row.warehouse)) return false;
      if (filterTypes.length > 0 && !filterTypes.includes(row.product_type)) return false;
      return true;
    });
  }, [data, filterMonths, filterDivisions, filterWarehouses, filterTypes]);

  const hasActiveFilters = filterMonths.length > 0 || filterDivisions.length > 0 || filterWarehouses.length > 0 || filterTypes.length > 0;

  const clearFilters = () => {
    setFilterMonths([]);
    setFilterDivisions([]);
    setFilterWarehouses([]);
    setFilterTypes([]);
  };

  const handleExport = () => {
    if (filteredData.length === 0) return;

    const exportData = filteredData.map(row => ({
      'Commercial Invoice': row.commercial_invoice,
      'Receipt Date': row.receipt_date,
      'Division': row.division,
      'Warehouse': row.warehouse,
      'Type': row.product_type || '',
      'Customer': row.customer || '',
      'Container': row.container || '',
      'Shipment': row.shipment_num || '',
      'Lines': row.line_count,
      'Units': Number(row.total_units) || 0,
      'Has Duty': row.has_duty ? 'Yes' : 'No',
      'Has Freight': row.has_freight ? 'Yes' : 'No',
      'Status': row.missing_data_status
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    ws['!cols'] = [
      { wch: 18 }, // Commercial Invoice
      { wch: 12 }, // Receipt Date
      { wch: 10 }, // Division
      { wch: 10 }, // Warehouse
      { wch: 12 }, // Type
      { wch: 20 }, // Customer
      { wch: 16 }, // Container
      { wch: 14 }, // Shipment
      { wch: 8 },  // Lines
      { wch: 10 }, // Units
      { wch: 10 }, // Has Duty
      { wch: 12 }, // Has Freight
      { wch: 22 }, // Status
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Missing Broker Data');
    XLSX.writeFile(wb, `Missing_Broker_Data_${filterMonths.length > 0 ? filterMonths.join('-') : 'all'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Missing Duty & Freight': 'bg-red-100 text-red-700 border-red-200',
      'Missing Duty': 'bg-amber-100 text-amber-700 border-amber-200',
      'Missing Freight': 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
        {status}
      </span>
    );
  };

  // Calculate totals from summary
  const totalMissing = summary.reduce((acc, s) => acc + (Number(s.total_missing) || 0), 0);
  const totalUnits = summary.reduce((acc, s) => acc + (Number(s.total_units_affected) || 0), 0);
  const totalMissingBoth = summary.reduce((acc, s) => acc + (Number(s.missing_both) || 0), 0);
  const totalMissingDuty = summary.reduce((acc, s) => acc + (Number(s.missing_duty_only) || 0), 0);
  const totalMissingFreight = summary.reduce((acc, s) => acc + (Number(s.missing_freight_only) || 0), 0);

  const containerClass = embedded
    ? 'space-y-4'
    : 'max-w-7xl mx-auto p-6 space-y-6';

  return (
    <div className={containerClass}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Missing Broker Data</h1>
            <p className="text-slate-500 mt-1">Invoices missing duty and/or freight information</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
            <FileText size={14} />
            Total Missing
          </div>
          <div className="text-2xl font-semibold text-slate-800">{totalMissing.toLocaleString()}</div>
          <div className="text-xs text-slate-400">invoices</div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
            <Package size={14} />
            Units Affected
          </div>
          <div className="text-2xl font-semibold text-slate-800">{totalUnits.toLocaleString()}</div>
          <div className="text-xs text-slate-400">total units</div>
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-4 bg-red-50/50">
          <div className="flex items-center gap-2 text-red-600 text-xs mb-1">
            <AlertTriangle size={14} />
            Missing Both
          </div>
          <div className="text-2xl font-semibold text-red-700">{totalMissingBoth.toLocaleString()}</div>
          <div className="text-xs text-red-400">duty & freight</div>
        </div>

        <div className="bg-white rounded-lg border border-amber-200 p-4 bg-amber-50/50">
          <div className="flex items-center gap-2 text-amber-600 text-xs mb-1">
            <DollarSign size={14} />
            Missing Duty
          </div>
          <div className="text-2xl font-semibold text-amber-700">{totalMissingDuty.toLocaleString()}</div>
          <div className="text-xs text-amber-400">duty only</div>
        </div>

        <div className="bg-white rounded-lg border border-blue-200 p-4 bg-blue-50/50">
          <div className="flex items-center gap-2 text-blue-600 text-xs mb-1">
            <Ship size={14} />
            Missing Freight
          </div>
          <div className="text-2xl font-semibold text-blue-700">{totalMissingFreight.toLocaleString()}</div>
          <div className="text-xs text-blue-400">freight only</div>
        </div>
      </div>

      {/* Monthly Summary */}
      {summary.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h3 className="font-medium text-slate-700 text-sm">By Month</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="px-4 py-2 text-left font-medium text-slate-600">Month</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-600">Total</th>
                  <th className="px-4 py-2 text-right font-medium text-red-600">Both</th>
                  <th className="px-4 py-2 text-right font-medium text-amber-600">Duty</th>
                  <th className="px-4 py-2 text-right font-medium text-blue-600">Freight</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-600">Units</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${filterMonths.includes(row.receipt_month) ? 'bg-blue-50' : ''}`}
                    onClick={() => {
                      if (filterMonths.includes(row.receipt_month)) {
                        setFilterMonths(filterMonths.filter(m => m !== row.receipt_month));
                      } else {
                        setFilterMonths([...filterMonths, row.receipt_month]);
                      }
                    }}
                  >
                    <td className="px-4 py-2 font-medium text-slate-700">{row.receipt_month}</td>
                    <td className="px-4 py-2 text-right text-slate-600">{Number(row.total_missing || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-red-600 font-medium">{Number(row.missing_both || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-amber-600">{Number(row.missing_duty_only || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-blue-600">{Number(row.missing_freight_only || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-slate-500 text-xs">{Number(row.total_units_affected || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Filter size={14} />
            Filters:
          </div>

          <MultiSelectDropdown
            label="Months"
            options={filterOptions.months}
            selected={filterMonths}
            onChange={setFilterMonths}
          />

          <MultiSelectDropdown
            label="Divisions"
            options={filterOptions.divisions}
            selected={filterDivisions}
            onChange={setFilterDivisions}
          />

          <MultiSelectDropdown
            label="Warehouses"
            options={filterOptions.warehouses}
            selected={filterWarehouses}
            onChange={setFilterWarehouses}
          />

          <MultiSelectDropdown
            label="Types"
            options={filterOptions.types}
            selected={filterTypes}
            onChange={setFilterTypes}
          />

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
            >
              <X size={12} />
              Clear All
            </button>
          )}

          <div className="flex-1" />

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={filteredData.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-800 text-white rounded hover:bg-slate-700 disabled:opacity-50"
          >
            <Download size={14} />
            Export
          </button>
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
            {filterMonths.map(month => (
              <span key={month} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs flex items-center gap-1">
                Month: {month}
                <button onClick={() => setFilterMonths(filterMonths.filter(m => m !== month))} className="hover:text-purple-900 ml-1">&times;</button>
              </span>
            ))}
            {filterDivisions.map(div => (
              <span key={div} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1">
                Div: {div}
                <button onClick={() => setFilterDivisions(filterDivisions.filter(d => d !== div))} className="hover:text-blue-900 ml-1">&times;</button>
              </span>
            ))}
            {filterWarehouses.map(wh => (
              <span key={wh} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                WH: {wh}
                <button onClick={() => setFilterWarehouses(filterWarehouses.filter(w => w !== wh))} className="hover:text-green-900 ml-1">&times;</button>
              </span>
            ))}
            {filterTypes.map(type => (
              <span key={type} className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs flex items-center gap-1">
                Type: {type}
                <button onClick={() => setFilterTypes(filterTypes.filter(t => t !== type))} className="hover:text-amber-900 ml-1">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h3 className="font-medium text-slate-700 text-sm">
            Missing Invoices
            <span className="ml-2 text-slate-400 font-normal">({filteredData.length.toLocaleString()} records)</span>
          </h3>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
            Loading...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            <AlertTriangle className="mx-auto mb-2" size={24} />
            {error}
          </div>
        ) : filteredData.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No missing broker data found
          </div>
        ) : (
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-2 text-left font-medium text-slate-600">Invoice</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-600">Receipt Date</th>
                  <th className="px-4 py-2 text-center font-medium text-slate-600">Div</th>
                  <th className="px-4 py-2 text-center font-medium text-slate-600">WH</th>
                  <th className="px-4 py-2 text-center font-medium text-slate-600">Type</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-600">Customer</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-600">Container</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-600">Shipment</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-600">Lines</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-600">Units</th>
                  <th className="px-4 py-2 text-center font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, idx) => (
                  <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{row.commercial_invoice}</td>
                    <td className="px-4 py-2 text-slate-600">{row.receipt_date}</td>
                    <td className="px-4 py-2 text-center text-slate-500">{row.division}</td>
                    <td className="px-4 py-2 text-center text-slate-500">{row.warehouse}</td>
                    <td className="px-4 py-2 text-center text-slate-500">{row.product_type}</td>
                    <td className="px-4 py-2 text-slate-600 truncate max-w-[150px]" title={row.customer}>{row.customer}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{row.container}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{row.shipment_num}</td>
                    <td className="px-4 py-2 text-right text-slate-600">{row.line_count}</td>
                    <td className="px-4 py-2 text-right text-slate-600">{Number(row.total_units || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-center">{getStatusBadge(row.missing_data_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
